export const TITLE_MAX_LENGTH = 100;
export const KEYWORD_MAX_LENGTH = 255;

import { BarChart3, KeyRound, Search, Users } from "lucide-react";

import { AI_ENGINES } from "@/services/rankTrackingService";

export const SETUP_ITEMS = [
  { icon: Search, text: "Dominio y motores de búsqueda" },
  { icon: KeyRound, text: "Keywords a monitorizar" },
  { icon: Users, text: "Competidores" },
  { icon: BarChart3, text: "Analítica" },
];

export const STEP_KEYS = ["general", "engines", "keywords", "competitors", "analytics", "review"];

export const FAMILY_LABELS = {
  google: "Google",
  bing: "Bing",
  yahoo: "Yahoo",
  ...Object.fromEntries(AI_ENGINES.map((engine) => [engine.id, engine.label])),
};

const AI_ENGINE_BY_ID = Object.fromEntries(AI_ENGINES.map((engine) => [engine.id, engine]));

/** In the draft an AI engine is an engine whose family is its `base_name`. */
export const isAiEngine = (engine) => Boolean(AI_ENGINE_BY_ID[engine?.family]);

export const isLocatedEngine = (family) => family === "google" || Boolean(AI_ENGINE_BY_ID[family]?.located);

/** Classic engines, the only ones that take keywords. */
export const keywordEngines = (engines) => (engines || []).filter((engine) => !isAiEngine(engine));

export const DEVICE_LABELS = { desktop: "Escritorio", mobile: "Móvil" };

const HOST_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

/** "https://www.Ejemplo.com/ruta?x" → "ejemplo.com"; "" when it is not a domain. */
export const extractHost = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "";
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
    const host = url.hostname.toLowerCase().replace(/\.$/, "").replace(/^www\./, "");
    return HOST_RE.test(host) ? host : "";
  } catch {
    return "";
  }
};

/** Normalised site URL: scheme + domain, no `www.` and no path. */
export const normalizeSiteUrl = (raw) => {
  const host = extractHost(raw);
  return host ? `https://${host}` : "";
};

export const normalizeKeyword = (raw) =>
  String(raw || "").trim().toLowerCase().replace(/\s+/g, " ");

export const splitLines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const slug = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Stable id of an engine in the draft, e.g. "google-desktop-es-madrid-spain-es". */
export const buildEngineKey = ({ family, device, country, region_name: regionName, lang_code: langCode }) =>
  [family, device || "desktop", String(country || "").toLowerCase(), slug(regionName), langCode || ""].join("-");

export const sameEngine = (a, b) =>
  (isAiEngine(a) || isAiEngine(b)
    ? a.family === b.family && String(a.country || "").toUpperCase() === String(b.country || "").toUpperCase()
    : String(a.search_engine_id) === String(b.search_engine_id)) &&
  (a.region_name || "") === (b.region_name || "") &&
  (a.lang_code || "") === (b.lang_code || "");

/** "Google · Móvil", "Bing", "Google AI Mode". */
export const engineTitle = (engine) => {
  if (isAiEngine(engine)) return AI_ENGINE_BY_ID[engine.family].title;
  const family = FAMILY_LABELS[engine.family] || engine.display?.engine || engine.family;
  return engine.family === "google" ? `${family} · ${DEVICE_LABELS[engine.device] || DEVICE_LABELS.desktop}` : family;
};

/** "España · Madrid · es". */
export const engineSubtitle = (engine) =>
  [
    engine.display?.country || engine.country,
    engine.display?.location || engine.region_name,
    engine.family === "google" || isAiEngine(engine) ? engine.display?.language || engine.lang_code : "",
  ]
    .filter(Boolean)
    .join(" · ");

export const emptyDraft = () => ({
  step: 0,
  general: { url: "", title: "" },
  engines: [],
  brand: "",
  keywords: {},
  competitors: [],
});

export const validateGeneral = (general) => {
  const errors = {};
  if (!String(general?.url || "").trim()) errors.url = "Indica la URL del sitio web.";
  else if (!extractHost(general.url)) errors.url = "Introduce una URL válida, por ejemplo ejemplo.com.";
  const title = String(general?.title || "").trim();
  if (!title) errors.title = "Indica un nombre para el proyecto.";
  else if (title.length > TITLE_MAX_LENGTH) errors.title = `Máximo ${TITLE_MAX_LENGTH} caracteres.`;
  return errors;
};

export const validateEngines = (engines, brand) => {
  const errors = {};
  if (!engines?.length) errors.list = "Añade al menos un motor de búsqueda.";
  if ((engines || []).some(isAiEngine) && !String(brand || "").trim()) {
    errors.brand = "Indica el nombre de la marca para los motores de IA.";
  }
  (engines || []).forEach((engine) => {
    if (engine.family === "google" && (!engine.country || !engine.lang_code)) {
      errors[engine.key] = "Falta el país o el idioma.";
    }
  });
  return errors;
};

export const validateKeywords = (engines, keywords, limit) => {
  const errors = {};
  keywordEngines(engines).forEach((engine) => {
    const count = (keywords?.[engine.key] || []).length;
    if (!count) errors[engine.key] = "Añade al menos una keyword.";
    else if (count > limit) errors[engine.key] = `Máximo ${limit} keywords por motor.`;
  });
  return errors;
};

export const validateCompetitors = (competitors, clientHost, max) => {
  const errors = {};
  if ((competitors || []).length > max) errors.list = `Máximo ${max} competidores.`;
  (competitors || []).forEach((domain) => {
    if (!extractHost(domain)) errors[domain] = "Dominio no válido.";
    else if (clientHost && extractHost(domain) === clientHost) errors[domain] = "Es el dominio del propio cliente.";
  });
  return errors;
};

export const validateStep = (stepKey, draft, limits) => {
  switch (stepKey) {
    case "general":
      return validateGeneral(draft.general);
    case "engines":
      return validateEngines(draft.engines, draft.brand);
    case "keywords":
      return validateKeywords(draft.engines, draft.keywords, limits.perEngine);
    case "competitors":
      return validateCompetitors(draft.competitors, extractHost(draft.general?.url), limits.competitors);
    default:
      return {};
  }
};

export const totalKeywordUnits = (engines, keywords) =>
  keywordEngines(engines).reduce((sum, engine) => sum + (keywords?.[engine.key] || []).length, 0);

/** The config's AI engines in the draft's shape. */
const aiEnginesFromConfig = (config) =>
  (config?.ai_engines || []).map(({ base_name: family, ...engine }) => ({ ...engine, family }));

/** The draft an edit starts from: the config the backend last applied. */
export const draftFromConfig = (config) => ({
  ...emptyDraft(),
  general: { url: config?.general?.url || "", title: config?.general?.title || "" },
  engines: [...(config?.engines || []).map((engine) => ({ ...engine })), ...aiEnginesFromConfig(config)],
  brand: config?.brand || "",
  keywords: { ...(config?.keywords || {}) },
  competitors: [...(config?.competitors || [])],
});

/** Engines and keyword x engine pairs removed lose their position history. */
export const diffRemovals = (config, payload) => {
  const before = [...(config?.engines || []), ...aiEnginesFromConfig(config)];
  const after = [...(payload?.engines || []), ...aiEnginesFromConfig(payload)];
  const engines = before.filter((engine) => !after.some((next) => sameEngine(engine, next)));
  const keywords = before.reduce((sum, engine) => {
    const kept = after.find((next) => sameEngine(engine, next));
    const now = new Set(kept ? payload.keywords?.[kept.key] || [] : []);
    return sum + (config.keywords?.[engine.key] || []).filter((word) => !now.has(word)).length;
  }, 0);
  const competitors = (config?.competitors || []).filter((domain) => !(payload?.competitors || []).includes(domain));
  return { engines, keywords, competitors };
};

/** The POST body: the draft without UI-only fields. */
export const buildProvisionPayload = (clientId, draft) => {
  const aiEngines = (draft.engines || []).filter(isAiEngine).map((engine) => ({
    key: engine.key,
    base_name: engine.family,
    country: engine.country,
    region_name: isLocatedEngine(engine.family) ? engine.region_name || "" : "",
    lang_code: engine.lang_code || "",
  }));
  const engines = keywordEngines(draft.engines).map((engine) => ({
    key: engine.key,
    family: engine.family,
    device: engine.device || "desktop",
    search_engine_id: engine.search_engine_id,
    country: engine.country,
    region_name: engine.family === "google" ? engine.region_name || "" : "",
    lang_code: engine.family === "google" ? engine.lang_code || "" : "",
  }));
  const keys = new Set(engines.map((engine) => engine.key));
  return {
    client: Number(clientId),
    general: {
      url: normalizeSiteUrl(draft.general?.url),
      title: String(draft.general?.title || "").trim(),
    },
    engines,
    ai_engines: aiEngines,
    brand: aiEngines.length ? String(draft.brand || "").trim() : "",
    keywords: Object.fromEntries(
      Object.entries(draft.keywords || {}).filter(([key]) => keys.has(key))
    ),
    competitors: (draft.competitors || []).map(extractHost).filter(Boolean),
  };
};

// Outlined MUI inputs with the look of CustomTextField.
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: "0.875rem",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000", borderWidth: 1 },
  },
  "& .MuiFormHelperText-root": { marginLeft: 0 },
};

// Bordered <ul> with dividers between rows.
export const listSx = {
  listStyle: "none",
  m: 0,
  p: 0,
  borderRadius: "12px",
  border: "1px solid #E8ECF2",
  overflow: "hidden",
  "& > li + li": { borderTop: "1px solid #F1F5F9" },
};
